import { getStandardPeriod, PERIOD_DEFINITIONS } from '@/lib/utils/historical-periods-shared';
import { BIBLE_BOOKS_MAP, type BibleBook } from '@/lib/utils/bible-utils';

/**
 * Helper function to find a Place for a GeographicEntity
 * Tries placeName first, then falls back to relatedPlaceNames
 */
export function findPlaceForEntity(
  entity: GeographicEntity,
  getPlaceByName: (placeName: string) => Place | undefined
): Place | undefined {
  // 1. Try exact placeName match
  let place = getPlaceByName(entity.placeName);
  if (place) return place;

  // 2. Try relatedPlaceNames (if available)
  if (entity.relatedPlaceNames && entity.relatedPlaceNames.length > 0) {
    for (const relatedName of entity.relatedPlaceNames) {
      place = getPlaceByName(relatedName);
      if (place) return place;
    }
  }

  return undefined;
}

/**
 * Select the best historical period for a place based on passage date
 * Uses testament-based heuristic with fallbacks
 */
function selectBestPeriod(
  place: Place,
  passageYear: number | null | undefined,
  testament: 'OT' | 'NT' | null
): HistoricalPeriod | null {
  const reconstructionsByPeriod = place.images?.reconstructionsByPeriod;
  if (!reconstructionsByPeriod) return null;

  const availablePeriods = Object.keys(reconstructionsByPeriod) as HistoricalPeriod[];
  if (availablePeriods.length === 0) return null;

  // If we have a passage year, find the matching period
  if (passageYear !== null && passageYear !== undefined) {
    const matchingPeriod = getStandardPeriod(passageYear);
    if (matchingPeriod && availablePeriods.includes(matchingPeriod)) {
      return matchingPeriod;
    }
  }

  // Testament-based heuristic
  if (testament === 'NT' && availablePeriods.includes('Roman Period')) {
    return 'Roman Period';
  }
  if (testament === 'OT' && availablePeriods.includes('Iron Age II')) {
    return 'Iron Age II';
  }

  // Fallback 1: Most recent period (likely most detailed)
  const sortedPeriods = availablePeriods.sort((a, b) => {
    const aEnd = PERIOD_DEFINITIONS[a]?.end || 0;
    const bEnd = PERIOD_DEFINITIONS[b]?.end || 0;
    return bEnd - aEnd; // Descending order (most recent first)
  });

  return sortedPeriods[0];
}

/**
 * Get the best transition image for a passage using hydrated Place data
 * Prioritizes primary place, then by type (city > region > landmark > route), then by importance
 * Selects period-specific reconstruction based on passage date
 * Returns both ruins and reconstruction image URLs and place name, or null if no suitable image is found
 *
 * @param placesByPassage - Geographic entities for the passage
 * @param getPlaceByName - Function to get Place data by name
 * @param getHistoricalEventsByPassageByPassageId - Function to get historical events by passage ID
 * @param getPassageById - Function to get passage by ID
 */
export function getBestTransitionImageFromPlaces(
  placesByPassage: PlacesByPassage | undefined,
  getPlaceByName: (placeName: string) => Place | undefined,
  getHistoricalEventsByPassageByPassageId: (passageId: string) => HistoricalEventsByPassage | undefined,
  getPassageById: (passageId: string) => Passage | undefined
): { ruinsImageUrl: string; reconstructionImageUrl: string; placeName: string; period?: HistoricalPeriod } | null {
  if (!placesByPassage || !placesByPassage.entities || placesByPassage.entities.length === 0) {
    return null;
  }

  // Get passage date and testament for period selection
  const historicalEventsByPassage = getHistoricalEventsByPassageByPassageId(placesByPassage.passageId);
  const passage = getPassageById(placesByPassage.passageId);
  const passageYear = historicalEventsByPassage?.passageDate?.startYear;

  // Determine testament from book name
  let testament: 'OT' | 'NT' | null = null;
  if (passage?.book) {
    const bookInfo = BIBLE_BOOKS_MAP.get(passage.book as BibleBook);
    if (bookInfo) {
      // NT books have sortOrder >= 40 (Matthew starts at 40)
      testament = bookInfo.sortOrder >= 40 ? 'NT' : 'OT';
    }
  }

  // Create a list of entities with their Place data, filtering for available images
  const entitiesWithImages = placesByPassage.entities
    .map((entity, index) => ({
      entity,
      place: findPlaceForEntity(entity, getPlaceByName),
      isPrimary: placesByPassage.primaryPlace?.entityIndex === index, // Mark primary
    }))
    .filter(({ place }) => {
      if (!place?.images?.ruinsImageUrl || place.images?.generationStatus !== 'completed') {
        return false;
      }

      // Check for period-specific reconstructions
      const hasPeriodReconstructions = place.images.reconstructionsByPeriod &&
                                       Object.keys(place.images.reconstructionsByPeriod).length > 0;

      return hasPeriodReconstructions;
    });

  if (entitiesWithImages.length === 0) {
    return null;
  }

  // Sort by priority: Primary place first, then type, then importance
  const priorityTypes = ['city', 'region', 'landmark', 'route'];
  const importanceOrder = { high: 0, medium: 1, low: 2 };

  entitiesWithImages.sort((a, b) => {
    // Primary place always wins
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;

    // Then by type
    const aTypeIndex = priorityTypes.indexOf(a.entity.type);
    const bTypeIndex = priorityTypes.indexOf(b.entity.type);
    const aIndex = aTypeIndex === -1 ? priorityTypes.length : aTypeIndex;
    const bIndex = bTypeIndex === -1 ? priorityTypes.length : bTypeIndex;

    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    // If same type, prefer higher importance
    const aImportance = importanceOrder[a.entity.importance] ?? 3;
    const bImportance = importanceOrder[b.entity.importance] ?? 3;
    return aImportance - bImportance;
  });

  const bestEntity = entitiesWithImages[0];
  const place = bestEntity.place!;

  // Select the best period for this passage
  const selectedPeriod = selectBestPeriod(place, passageYear, testament);

  // Get reconstruction for the selected period
  if (!selectedPeriod || !place.images!.reconstructionsByPeriod?.[selectedPeriod]) {
    // No reconstruction available for this period
    return null;
  }

  const reconstructionImageUrl = place.images!.reconstructionsByPeriod[selectedPeriod]!.imageUrl;

  return {
    ruinsImageUrl: place.images!.ruinsImageUrl!,
    reconstructionImageUrl,
    placeName: bestEntity.entity.placeName,
    period: selectedPeriod
  };
}
